import type React from "react"
import { Link } from "react-router-dom"
import Navbar from "../../components/Navbar"
import Footer from "../../components/Footer"
import { CheckCircle, Building, Users, Shield, Clock, MapPin, Truck } from "lucide-react"

const MunicipalServicesPage: React.FC = () => {
  const applications = [
    {
      title: "Street Cleaning & Sanitation",
      description: "Comprehensive street cleaning, debris removal, and urban sanitation services",
      image: "https://placehold.co/600x400?text=Street+Cleaning",
      attachments: ["Street Cleaner", "CityVac Garbage Vacuum", "High Volume Sweeper", "Surface Cleaner"],
      benefits: ["Improved public health", "Enhanced city appearance", "Reduced maintenance costs"],
    },
    {
      title: "Parks & Recreation Maintenance",
      description: "Professional maintenance of public parks, sports fields, and recreational facilities",
      image: "https://placehold.co/600x400?text=Parks+Maintenance",
      attachments: ["Cylinder Mower", "Rotary Mower", "Leaf Blower", "Watering Arm"],
      benefits: ["Beautiful public spaces", "Safe recreation areas", "Community pride"],
    },
    {
      title: "Public Works Operations",
      description: "Infrastructure maintenance, road repair preparation, and utility support",
      image: "https://placehold.co/600x400?text=Public+Works",
      attachments: ["Patch Planer", "High Tip Bucket", "Manhole Cutter", "Gully Cleaner"],
      benefits: ["Extended infrastructure life", "Reduced emergency repairs", "Cost-effective maintenance"],
    },
    {
      title: "Facility Management",
      description: "Maintenance of municipal buildings, schools, and government facilities",
      image: "https://placehold.co/600x400?text=Facility+Management",
      attachments: ["Front Sweeper", "Pressure Washer", "Snow Brush", "Salt Spreader"],
      benefits: ["Professional appearance", "Safe environments", "Operational efficiency"],
    },
    {
      title: "Emergency Response Support",
      description: "Rapid deployment for emergency cleanup, storm response, and disaster recovery",
      image: "https://placehold.co/600x400?text=Emergency+Response",
      attachments: ["High Tip Bucket", "Debris Collector", "Emergency Lighting", "Barrier Mover"],
      benefits: ["Quick response times", "Versatile capabilities", "Community safety"],
    },
    {
      title: "Seasonal Operations",
      description: "Year-round municipal services including winter maintenance and seasonal cleanup",
      image: "https://placehold.co/600x400?text=Seasonal+Operations",
      attachments: ["Snow Plough", "Salt Spreader", "Leaf Collector", "De-icing Sprayer"],
      benefits: ["All-weather capability", "Seasonal adaptability", "Continuous service"],
    },
  ]

  const municipalBenefits = [
    {
      icon: Users,
      title: "Citizen Satisfaction",
      description: "Clean, well-maintained public spaces improve quality of life for residents",
    },
    {
      icon: Shield,
      title: "Public Safety",
      description: "Proper maintenance reduces hazards and creates safer environments",
    },
    {
      icon: Clock,
      title: "Operational Efficiency",
      description: "One versatile machine handles multiple municipal tasks efficiently",
    },
    {
      icon: MapPin,
      title: "Wide Coverage",
      description: "Compact design allows access to all areas of the municipality",
    },
  ]

  const budgetBenefits = [
    {
      title: "Reduced Equipment Costs",
      description: "One machine replaces multiple specialized vehicles",
      savings: "Up to 40%",
    },
    {
      title: "Lower Maintenance Costs",
      description: "Single maintenance program vs. multiple vehicle maintenance",
      savings: "Up to 30%",
    },
    {
      title: "Operational Savings",
      description: "Reduced fuel, insurance, and operator training costs",
      savings: "Up to 25%",
    },
    {
      title: "Storage Efficiency",
      description: "Compact storage requirements with quick-change attachments",
      savings: "Up to 50%",
    },
  ]

  const caseStudies = [
    {
      municipality: "City of Ballarat",
      population: "110,000",
      challenge: "Needed versatile equipment for year-round municipal maintenance with limited budget",
      solution: "Multihog CX with 8 specialized attachments",
      results: [
        "35% reduction in equipment costs",
        "Improved response times for emergency cleanup",
        "Enhanced public space maintenance quality",
        "Simplified operator training and certification",
      ],
    },
    {
      municipality: "Shire of Bendigo",
      population: "120,000",
      challenge: "Required efficient solution for parks maintenance and street cleaning",
      solution: "Multihog MX with ground care and cleaning attachments",
      results: [
        "50% faster park maintenance completion",
        "Improved street cleanliness ratings",
        "Reduced fuel consumption by 25%",
        "Enhanced community satisfaction scores",
      ],
    },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6 inline-block bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-full text-sm font-medium">
              Municipal Services
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Complete
              <br />
              <span className="text-purple-400">Municipal Solutions</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Comprehensive municipal maintenance solutions for cities and towns. From street cleaning to parks
              maintenance, deliver exceptional public services with versatile, cost-effective equipment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                Get Municipal Quote
              </Link>
              <Link
                to="/models"
                className="border border-white text-white hover:bg-white hover:text-purple-900 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                View Municipal Models
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Municipal Stats */}
      <section className="py-16 bg-purple-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Trusted by Municipalities Nationwide</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="text-3xl font-bold text-purple-600 mb-2">150+</div>
                <div className="text-gray-600">Municipalities Served</div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="text-3xl font-bold text-purple-600 mb-2">40%</div>
                <div className="text-gray-600">Average Cost Savings</div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="text-3xl font-bold text-purple-600 mb-2">365</div>
                <div className="text-gray-600">Days Per Year Service</div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="text-3xl font-bold text-purple-600 mb-2">95%</div>
                <div className="text-gray-600">Customer Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Municipal Applications</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive solutions for all municipal maintenance needs, designed to improve efficiency, reduce
                costs, and enhance community satisfaction.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {applications.map((app, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                >
                  <img src={app.image || "/placeholder.svg"} alt={app.title} className="w-full h-48 object-cover" />
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{app.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{app.description}</p>

                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Key Benefits:</h4>
                      <ul className="space-y-1">
                        {app.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <CheckCircle className="h-4 w-4 text-purple-600 mr-2 flex-shrink-0" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Recommended Attachments:</h4>
                      <div className="flex flex-wrap gap-2">
                        {app.attachments.map((attachment, idx) => (
                          <span
                            key={idx}
                            className="inline-block bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs"
                          >
                            {attachment}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Municipalities Choose Multihog</h2>
              <p className="text-lg text-gray-600">
                Experience the advantages that make Multihog the preferred choice for municipal operations
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {municipalBenefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="bg-purple-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <benefit.icon className="h-8 w-8 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>

            {/* Budget Benefits */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Municipal Budget Benefits</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {budgetBenefits.map((benefit, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl font-bold text-purple-600 mb-2">{benefit.savings}</div>
                    <h4 className="font-semibold text-gray-900 mb-2">{benefit.title}</h4>
                    <p className="text-gray-600 text-sm">{benefit.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Municipal Success Stories</h2>
              <p className="text-lg text-gray-600">
                Real results from municipalities that have transformed their operations with Multihog
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {caseStudies.map((study, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                  <div className="flex items-center mb-6">
                    <Building className="h-8 w-8 text-purple-600 mr-3" />
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{study.municipality}</h3>
                      <p className="text-gray-600">Population: {study.population}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Challenge:</h4>
                    <p className="text-gray-600 mb-4">{study.challenge}</p>

                    <h4 className="font-semibold text-gray-900 mb-2">Solution:</h4>
                    <p className="text-purple-600 font-medium mb-4">{study.solution}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Results:</h4>
                    <ul className="space-y-2">
                      {study.results.map((result, idx) => (
                        <li key={idx} className="flex items-center text-gray-600">
                          <CheckCircle className="h-4 w-4 text-purple-600 mr-2 flex-shrink-0" />
                          {result}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Municipal Financing Section */}
      <section className="py-20 bg-purple-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Municipal Financing Options</h2>
            <p className="text-lg text-gray-600 mb-8">
              We understand municipal budget constraints. That's why we offer flexible financing solutions to help you
              get the equipment you need within your budget.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <Truck className="h-8 w-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Lease Options</h3>
                <p className="text-gray-600 text-sm">Flexible lease terms to fit your budget cycle</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <Clock className="h-8 w-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Rental Programs</h3>
                <p className="text-gray-600 text-sm">Short-term rentals for seasonal or project needs</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <Shield className="h-8 w-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Service Packages</h3>
                <p className="text-gray-600 text-sm">Comprehensive service and maintenance packages</p>
              </div>
            </div>

            <Link
              to="/contact"
              className="inline-block bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-lg font-medium transition-colors"
            >
              Discuss Financing Options
            </Link>
          </div>
        </div>
      </section>

      {/* Training & Support Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Complete Municipal Support</h2>
              <p className="text-lg text-gray-600">
                We provide comprehensive support to ensure your municipal operations run smoothly
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-purple-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Users className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Operator Training</h3>
                <p className="text-gray-600">
                  Comprehensive training programs for your municipal staff, including safety protocols and efficient
                  operation techniques.
                </p>
              </div>
              <div className="text-center">
                <div className="bg-purple-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Shield className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Maintenance Support</h3>
                <p className="text-gray-600">
                  Scheduled maintenance programs, parts supply, and technical support to keep your equipment running
                  optimally.
                </p>
              </div>
              <div className="text-center">
                <div className="bg-purple-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Clock className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">24/7 Support</h3>
                <p className="text-gray-600">
                  Round-the-clock technical support for emergency situations and urgent municipal service needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-purple-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Transform Your Municipal Operations</h2>
            <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
              Join the growing number of municipalities that have revolutionized their operations with Multihog
              solutions. Improve efficiency, reduce costs, and enhance community satisfaction.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                Schedule Municipal Consultation
              </Link>
              <Link
                to="/models"
                className="border border-white text-white hover:bg-white hover:text-purple-600 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                View Municipal Models
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default MunicipalServicesPage
